import type { PropsWithChildren, JSX } from 'react';
import { createContext, useContext } from 'react';

/**
 * Marks a surface whose tiles take part in the listing→detail view
 * transitions (hero morph, overlay sheet, cross-fade). Category and browse
 * pages wrap their content in {@link TileViewTransitionScope}; tiles rendered
 * anywhere else — homepage rows, detail-page related grids, recently-viewed
 * strips on non-listing pages — fall outside the scope and navigate plainly,
 * so a related tile on a detail page breaks out to the full page instead of
 * opening another sheet.
 *
 * The scope is necessary but not sufficient: the `details_transition_2026`
 * flag (`uiContextAtomViewTransitionsEnabled`) still gates everything.
 */
const TileViewTransitionScopeContext = createContext<boolean>(false);

/** Whether tiles on this surface take part in the detail view transitions. */
export const useTileViewTransitionScope = (): boolean =>
    useContext(TileViewTransitionScopeContext);

/** Wraps a listing surface whose tiles opt into the detail transitions. */
export const TileViewTransitionScope = ({
    children,
}: PropsWithChildren): JSX.Element => (
    <TileViewTransitionScopeContext.Provider value={true}>
        {children}
    </TileViewTransitionScopeContext.Provider>
);
